import { Head } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import { Clock, User, FileText, DollarSign, Award, Shield, UserCheck } from 'lucide-react'

interface ActivityLog {
  id: number
  action: string
  target_type: string
  target_id: number
  metadata: {
    model_name: string
    record_id: number
    record_display: string
    [key: string]: any
  }
  user_name: string
  user_email: string
  ip_address: string
  created_at: string
}

interface Props extends PageProps {
  logs: ActivityLog[]
}

const actionColors: Record<string, string> = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700'
}

const modelIcons: Record<string, React.ComponentType<any>> = {
  Agent: UserCheck,
  Commission: DollarSign,
  EquityCredit: Award,
  AgentLicense: Shield,
  User: User
}

export default function ActivityLogsIndex({ logs }: Props) {
  const formatChanges = (metadata: ActivityLog['metadata']) => {
    const changes = Object.entries(metadata)
      .filter(([key]) => !['model_name', 'record_id', 'record_display'].includes(key))
    
    if (changes.length === 0) return null
    
    return changes.slice(0, 3).map(([key, value]) => {
      if (typeof value === 'object' && value !== null && 'from' in value && 'to' in value) {
        return (
          <div key={key} className="text-xs text-chestnut-charcoal/60">
            <span className="font-medium">{key}:</span> {String(value.from || '-')} → {String(value.to || '-')}
          </div>
        )
      }
      return null
    }).filter(Boolean)
  }

  const columns: Column<ActivityLog>[] = [
    {
      key: 'created_at',
      header: 'Time',
      sortable: true,
      render: (log) => (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-chestnut-charcoal/40" />
          <div>
            <div className="text-sm font-medium">{new Date(log.created_at).toLocaleDateString()}</div>
            <div className="text-xs text-chestnut-charcoal/60">{new Date(log.created_at).toLocaleTimeString()}</div>
          </div>
        </div>
      )
    },
    {
      key: 'user_name',
      header: 'User',
      sortable: true,
      render: (log) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{log.user_name}</div>
          <div className="text-xs text-chestnut-charcoal/60">{log.user_email}</div>
        </div>
      )
    },
    {
      key: 'action',
      header: 'Action',
      sortable: true,
      render: (log) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full uppercase ${actionColors[log.action] || 'bg-gray-100 text-gray-700'}`}>
          {log.action}
        </span>
      )
    },
    {
      key: 'target_type',
      header: 'Record',
      sortable: true,
      render: (log) => {
        const Icon = modelIcons[log.metadata?.model_name] || FileText
        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-chestnut-forest" />
            <div>
              <div className="font-medium text-chestnut-charcoal">{log.metadata?.model_name || log.target_type}</div>
              <div className="text-xs text-chestnut-charcoal/60">{log.metadata?.record_display || `#${log.target_id}`}</div>
            </div>
          </div>
        )
      }
    },
    {
      key: 'metadata',
      header: 'Changes',
      render: (log) => (
        <div className="max-w-xs">{formatChanges(log.metadata)}</div>
      )
    },
    {
      key: 'ip_address',
      header: 'IP Address',
      className: 'text-chestnut-charcoal/60 text-xs font-mono'
    }
  ]

  return (
    <AppLayout>
      <Head title="Activity Logs" />

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Activity Logs</h1>
          <p className="text-chestnut-charcoal/70 mt-1">View all changes made to records in the system</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            Activity logs are read-only and cannot be modified. They provide a complete audit trail of all changes made to agents, commissions, equity credits, and licenses.
          </p>
        </div>

        <div className="card p-6">
          <DataTable
            data={logs}
            columns={columns}
            searchPlaceholder="Search activity logs..."
            searchKeys={['user_name', 'user_email', 'action', 'target_type']}
            pageSize={20}
            emptyMessage="No activity recorded yet."
            getRowKey={(log) => log.id}
          />
        </div>
      </div>
    </AppLayout>
  )
}
